import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Camera, Hash, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppStore } from "../store/useAppStore";
import { visitorAuthService } from "../services/visitorAuthService";
import { visitorSessionService, mapAuthUserToProfile } from "../services/visitorSessionService";
import { eventsService } from "../services/eventsService";
import { maxPlatform } from "../services/maxPlatform";
import { ApiError, formatApiError } from "../api/client";
import { CodeScanner, type CodeScanResult } from "../components/CodeScanner";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import {
  bindCameraGrantToUser,
  isCameraGrantedForUser,
  markCameraGrantedForUser,
} from "../services/cameraPermission";
import {
  isCameraApiAvailable,
  isNotAllowedError,
  openCameraStream,
  stopMediaStream,
} from "../services/cameraStream";
import { debugLog } from "../services/debugLog";

/**
 * Logged-in path: claim exhibitor (or visitor) access by scanning a badge barcode
 * with the current MAX/personal phone — needed when secretary registered everyone
 * on a booth line and the user already has visitor access to another show.
 */
export function ClaimExhibitorBadgePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, updateUser, selectEvent, completeOnboarding, setEventRole } = useAppStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scanPaused, setScanPaused] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [cameraArmed, setCameraArmed] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [badgeStream, setBadgeStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    return () => {
      stopMediaStream(streamRef.current);
      streamRef.current = null;
    };
  }, []);

  const enableCamera = async () => {
    if (!isCameraApiAvailable()) {
      setCameraError(t("visitor.camera_open_failed"));
      return;
    }
    setCameraStarting(true);
    setCameraError("");
    setCameraDenied(false);
    try {
      const stream = await openCameraStream({ preferBack: true });
      stopMediaStream(streamRef.current);
      streamRef.current = stream;
      setBadgeStream(stream);
      setCameraArmed(true);
      markCameraGrantedForUser(user.id || null);
    } catch (err) {
      if (isNotAllowedError(err)) setCameraDenied(true);
      setCameraError(t("visitor.camera_needed"));
      setCameraArmed(false);
    } finally {
      setCameraStarting(false);
    }
  };

  const submitCode = async (raw: string) => {
    const code = raw.trim().replace(/\s+/g, "");
    if (!code || loading || submittingRef.current) return;
    submittingRef.current = true;
    setScanPaused(true);
    setLoading(true);
    setError("");
    try {
      let contact = visitorAuthService.getCachedMaxContact();
      // Prefer session phone; request MAX contact only if we still need a number.
      let phone = user.phone?.trim() || contact?.phone;
      if (!phone && maxPlatform.canRequestContact()) {
        const c = await maxPlatform.requestContact();
        phone = c.phone;
        contact = { phone: c.phone, authDate: c.authDate, hash: c.hash };
      }
      if (!phone) {
        setError(t("claim_badge.err_phone"));
        setScanPaused(false);
        return;
      }

      debugLog.info("auth", "claim exhibitor badge", {
        len: code.length,
        phoneTail: phone.slice(-4),
      });

      const session = await visitorAuthService.loginWithBadge(code, {
        phone,
        contact: contact?.authDate && contact?.hash ? contact : undefined,
      });

      updateUser(mapAuthUserToProfile(session.user));
      try {
        await visitorSessionService.syncProfile(updateUser);
      } catch {
        /* non-fatal */
      }
      bindCameraGrantToUser(session.user.id);

      const eventId = session.primaryEventId;
      if (eventId) {
        selectEvent(eventId);
        completeOnboarding();
        eventsService.clearCache();
        await visitorSessionService.syncEventRole(eventId, setEventRole).catch(() => undefined);
      }
      toast.success(t("claim_badge.success"));
      maxPlatform.hapticFeedback("medium");
      navigate("/home", { replace: true });
    } catch (err) {
      debugLog.error("auth", "claim badge failed", err);
      setError(
        err instanceof ApiError
          ? formatApiError(err, t("claim_badge.err_generic"))
          : t("claim_badge.err_generic"),
      );
      setScanPaused(false);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const onScan = async (result: CodeScanResult) => {
    if (loading || scanPaused) return;
    await submitCode(result.text);
  };

  return (
    <div className="min-h-full bg-gradient-to-b from-indigo-50 via-white to-slate-50 flex flex-col">
      <div className="w-full max-w-lg mx-auto flex flex-col flex-1 min-h-0">
        <div className="shrink-0 pt-[max(0.75rem,env(safe-area-inset-top))] px-4 pb-2">
          <div className="flex items-center justify-between gap-3 min-h-9">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 -ml-1 px-1 py-1.5 text-[13px] text-gray-500"
            >
              <ArrowLeft size={16} /> {t("login.back")}
            </button>
            <LanguageSwitcher />
          </div>
          <h1 className="text-[22px] text-gray-900 mt-2 mb-1 text-center">
            {t("claim_badge.title")}
          </h1>
          <p className="text-[13px] text-gray-500 text-center mb-2 px-2 leading-relaxed">
            {t("claim_badge.subtitle")}
          </p>
          {user.phone ? (
            <p className="text-[12px] text-indigo-600 text-center mb-2">
              {t("claim_badge.phone_line", { phone: user.phone })}
            </p>
          ) : null}
        </div>

        <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-3">
          <div className="relative isolate w-full h-[min(42vh,280px)] rounded-2xl overflow-hidden border border-gray-100 bg-black">
            {!cameraArmed || !badgeStream ? (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 p-5 text-center bg-slate-900">
                <Camera size={36} className="text-indigo-300" />
                <p className="text-[15px] text-white font-semibold">
                  {t("claim_badge.camera_title")}
                </p>
                <p className="text-[12px] text-white/80 leading-relaxed max-w-xs">
                  {cameraDenied
                    ? t("visitor.camera_settings_hint")
                    : t("claim_badge.camera_hint")}
                </p>
                {cameraError ? <p className="text-[12px] text-red-300">{cameraError}</p> : null}
                <button
                  type="button"
                  onClick={() => void enableCamera()}
                  disabled={cameraStarting}
                  className="w-full max-w-xs rounded-xl bg-indigo-500 px-4 py-3 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  {cameraStarting ? t("visitor.camera_requesting") : t("scan.allow_camera")}
                </button>
              </div>
            ) : (
              <CodeScanner
                active
                variant="badge"
                initialStream={badgeStream}
                paused={scanPaused || loading}
                onScan={onScan}
                className="absolute inset-0 z-0 !h-full !w-full !min-h-full !rounded-none"
              />
            )}
            {loading ? (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50">
                <Loader2 className="animate-spin text-white" size={28} />
              </div>
            ) : null}
          </div>

          {error ? (
            <p className="text-[13px] text-red-600 text-center leading-snug">{error}</p>
          ) : null}

          <div className="rounded-2xl border border-gray-100 bg-white p-3 space-y-2">
            <label className="flex items-center gap-2 text-[12px] text-gray-500">
              <Hash size={14} /> {t("login.enter_digits_label")}
            </label>
            <input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder={t("login.enter_digits_placeholder")}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[15px] outline-none focus:border-indigo-400"
              inputMode="numeric"
              autoComplete="off"
            />
            <button
              type="button"
              disabled={loading || !manualCode.trim()}
              onClick={() => void submitCode(manualCode)}
              className="w-full rounded-xl bg-indigo-500 py-3 text-[14px] font-semibold text-white disabled:opacity-50"
            >
              {t("claim_badge.submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
